import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WideLayout } from '../../../shared/layout/widelayout';
import {
  createPatientV2TriageNote,
  savePatientV2RegulatoryDecision,
  getPatientV2ById,
  getPatientV2Triage,
  reviewPatientV2,
  resubmitPatientV2,
  type PatientV2DetailResponse,
  type PatientV2ReviewResponse,
  type PatientV2ReviewPayload,
  type PatientV2ResubmitPayload,
  type PatientV2TriageItemResponse,
  type PatientV2TriageNotePayload,
  type PatientV2RegulatoryDecisionPayload,
} from '../../../services/patients/patientsV2Service';
import { PatientDetailsLayout } from '../usecases/v2/PatientDetailsLayout';
import { canReviewPatientsV2, type PatientDetailsMode, type PatientV2Detail } from '../usecases/v2/types';
import { usePortalStore } from '../../../stores/portalStore';

const joinFilled = (values: Array<string | undefined>) =>
  values
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(' · ');

const formatFileSize = (size: number) => {
  if (!size) return 'Tamanho não informado';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const toPatientDetail = (patient: PatientV2DetailResponse, reviewResult?: PatientV2ReviewResponse): PatientV2Detail => ({
  id: patient.id,
  patient: patient.name,
  cns: '',
  urs: '',
  crs: '',
  requestedAt: patient.clinicalInformation?.requestDate ?? '',
  sla: '',
  slaTone: 'info',
  regulatoryStatus: patient.regulatoryStatus,
  operationalStatus: patient.operationalStatus,
  sap: patient.sap,
  cpf: patient.cpf,
  birthDate: patient.birthDate,
  email: patient.email,
  cellphone: patient.cellphone,
  phoneNumber: patient.cellphone,
  phones: joinFilled([patient.cellphone, patient.cellphone2]),
  cep: patient.cep,
  city: patient.city,
  uf: patient.uf,
  address: patient.address,
  number: patient.number,
  neighborhood: patient.neighborhood,
  addressLine2: patient.addressLine2,
  companyId: patient.company.id,
  company: patient.company,
  canEdit: patient.canEdit,
  sts: '',
  doctor: patient.clinicalInformation?.requestingDoctorName ?? '',
  protocol: patient?.history[0].requestId,
  clinical: {
    cidPrimary: patient.clinicalInformation?.cidPrimary ?? '',
    cidSecondary: patient.clinicalInformation?.cidSecondary ?? '',
    diagnosis: patient.clinicalInformation?.clinicalDiagnosis ?? '',
    oxygenIndication: patient.clinicalInformation?.oxygenIndication ?? '',
    oxygenSource: patient.clinicalInformation?.prescribedOxygenSource ?? '',
    oxygenFlow: patient.clinicalInformation?.oxygenFlowLMin ?? '',
    dailyUse: patient.clinicalInformation?.dailyUseHours ?? '',
    requestedEquipment: patient.clinicalInformation?.requestedEquipment ?? '',
    requestingDoctorName: patient.clinicalInformation?.requestingDoctorName ?? '',
    requestingDoctorCrm: patient.clinicalInformation?.requestingDoctorCrm ?? '',
    requestDate: patient.clinicalInformation?.requestDate ?? '',
    reviewStatus: patient.clinicalInformation?.reviewStatus,
    reviewNotes: patient.clinicalInformation?.reviewNotes,
  },
  documents: (patient.documents ?? []).map((document) => ({
    id: Number(document.id),
    name: document.originalFileName,
    type: document.documentType || 'Não informado',
    size: formatFileSize(document.fileSizeBytes),
    status: document.reviewStatus,
    tone: ['Aprovado', 'Validado'].includes(document.reviewStatus) ? 'success' : document.reviewStatus === 'Pendente' ? 'warning' : 'info',
    reviewReason: document.reviewReason,
    uploadedAt: document.uploadedAt ?? undefined,
    documentGeneratedAt: document.documentGeneratedAt ?? undefined,
    documentExpiresAt: document.documentExpiresAt ?? undefined,
  })),
  history: [
    ...(patient.history ?? []),
    ...(reviewResult?.historyItem && !(patient.history ?? []).some((item) => String(item.id) === String(reviewResult.historyItem.id))
      ? [
          {
            id: reviewResult.historyItem.id,
            eventType: reviewResult.historyItem.eventType,
            regulatoryStatus: reviewResult.historyItem.regulatoryStatus,
            decision: reviewResult.historyItem.decision,
            createdAt: reviewResult.historyItem.createdAt,
          },
        ]
      : []),
  ],
  approvalSections: patient.review,
  review: {
    sectionStatus: {},
    issues: (patient.review?.issues ?? []).map((issue) => ({
      section: issue.section,
      field: issue.field,
      label: issue.label,
      description: issue.description,
      documentId: issue.documentId,
    })),
    documents: [],
  },
  canStartReview: patient.review?.canStartReview,
  canMakeRegulatoryDecision: patient.review?.canMakeRegulatoryDecision,
  regulatoryDecision: patient.regulatoryDecision ?? null,
});

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Não foi possível ler o arquivo ${file.name}.`));
    reader.onload = () =>
      resolve(
        String(reader.result ?? '')
          .split(',')
          .pop() ?? ''
      );
    reader.readAsDataURL(file);
  });

const DetailFeedback = ({ children, retry }: { children: React.ReactNode; retry?: () => void }) => (
  <Box sx={{ minHeight: '100%', p: { xs: 2, md: 3 }, backgroundColor: '#ececec' }}>
    <Paper sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
      {children}
      {retry && (
        <Button sx={{ mt: 2 }} variant="contained" onClick={retry}>
          Tentar novamente
        </Button>
      )}
    </Paper>
  </Box>
);

export const PatientV2DetailsPage = ({ mode = 'view' }: { mode?: PatientDetailsMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams<{ patientId: string }>();
  const currentUser = usePortalStore((state: any) => state.currentUser);
  const [patient, setPatient] = useState<PatientV2Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [triageItems, setTriageItems] = useState<PatientV2TriageItemResponse[]>([]);
  const [triageLoading, setTriageLoading] = useState(mode === 'review');
  const [triageError, setTriageError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!patientId) {
        setLoading(false);
        setNotFound(true);
        return;
      }

      setLoading(true);
      setError('');
      setNotFound(false);
      setTriageError('');
      if (mode === 'review') setTriageLoading(true);
      try {
        const response = await getPatientV2ById(patientId);
        const reviewResult = location.state?.patientV2ReviewResult as PatientV2ReviewResponse | undefined;
        if (active) setPatient(toPatientDetail(response, reviewResult));
        if (mode === 'review') {
          try {
            const triage = await getPatientV2Triage(patientId);
            if (active) setTriageItems(triage);
          } catch (triageRequestError: any) {
            if (active) {
              setTriageItems([]);
              setTriageError(triageRequestError?.message ?? 'Não foi possível carregar a triagem.');
            }
          }
        }
      } catch (requestError: any) {
        if (!active) return;
        setPatient(null);
        if (requestError?.status === 404) setNotFound(true);
        else setError(requestError?.message ?? 'Não foi possível carregar o paciente.');
      } finally {
        if (active) {
          setLoading(false);
          setTriageLoading(false);
        }
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [location.state, mode, patientId, retryKey]);

  const refreshTriage = async () => {
    if (!patientId || mode !== 'review') return;
    setTriageLoading(true);
    setTriageError('');
    try {
      setTriageItems(await getPatientV2Triage(patientId));
    } catch (requestError: any) {
      setTriageError(requestError?.message ?? 'Não foi possível carregar a triagem.');
    } finally {
      setTriageLoading(false);
    }
  };

  const content = loading ? (
    <DetailFeedback>
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    </DetailFeedback>
  ) : mode === 'review' && !canReviewPatientsV2(currentUser?.patientV2Profile) ? (
    <DetailFeedback>
      <Alert severity="warning">A análise de solicitações está disponível apenas para os perfis avaliadores de Pacientes V2.</Alert>
      <Button sx={{ mt: 2 }} onClick={() => navigate(`/patientsv2/${patientId}`)}>
        Visualizar paciente
      </Button>
    </DetailFeedback>
  ) : notFound ? (
    <DetailFeedback>
      <Alert severity="warning">Paciente não encontrado ou não disponível para o seu acesso.</Alert>
      <Button sx={{ mt: 2 }} onClick={() => navigate('/patientsv2')}>
        Voltar para pacientes
      </Button>
    </DetailFeedback>
  ) : error ? (
    <DetailFeedback retry={() => setRetryKey((value) => value + 1)}>
      <Alert severity="error">{error}</Alert>
    </DetailFeedback>
  ) : (
    <PatientDetailsLayout
      mode={mode}
      patient={patient}
      readOnly={mode === 'review' || !patient?.canEdit}
      onBack={() => navigate('/patientsv2')}
      onResubmit={
        mode === 'view'
          ? async (submittedPatient, documentReplacements) => {
              if (!patientId) return;
              const replacements = await Promise.all(
                documentReplacements.map(async ({ replacedDocumentId, file }) => ({
                  replacedDocumentId,
                  documentType: file.documentType,
                  customDocumentType: file.customDocumentType,
                  documentGeneratedAt: file.documentGeneratedAt,
                  originalFileName: file.name,
                  mimeType: file.type,
                  fileSizeBytes: file.size,
                  contentBase64: await readFileAsBase64(file),
                }))
              );
              const payload: PatientV2ResubmitPayload = {
                patient: submittedPatient.patient,
                sap: submittedPatient.sap,
                cpf: submittedPatient.cpf,
                birthDate: submittedPatient.birthDate,
                email: submittedPatient.email,
                cellphone: submittedPatient.cellphone,
                phoneNumber: submittedPatient.phoneNumber,
                cep: submittedPatient.cep,
                address: submittedPatient.address,
                number: submittedPatient.number,
                addressLine2: submittedPatient.addressLine2,
                neighborhood: submittedPatient.neighborhood,
                city: submittedPatient.city,
                uf: submittedPatient.uf,
                companyId: submittedPatient.companyId,
                clinicalInformation: {
                  cidPrimary: submittedPatient.clinical.cidPrimary,
                  cidSecondary: submittedPatient.clinical.cidSecondary,
                  clinicalDiagnosis: submittedPatient.clinical.diagnosis,
                  oxygenIndication: submittedPatient.clinical.oxygenIndication,
                  prescribedOxygenSource: submittedPatient.clinical.oxygenSource,
                  oxygenFlowLMin: submittedPatient.clinical.oxygenFlow,
                  dailyUseHours: submittedPatient.clinical.dailyUse,
                  requestedEquipment: submittedPatient.clinical.requestedEquipment,
                  requestingDoctorName: submittedPatient.clinical.requestingDoctorName,
                  requestingDoctorCrm: submittedPatient.clinical.requestingDoctorCrm,
                  requestDate: submittedPatient.clinical.requestDate,
                },
                documentReplacements: replacements,
              };
              console.log(submittedPatient)
              await resubmitPatientV2(patientId, payload);
              setRetryKey((value) => value + 1);
            }
          : undefined
      }
      onReview={
        mode === 'review'
          ? async (payload: PatientV2ReviewPayload) => {
              if (!patientId) return;
              await reviewPatientV2(patientId, payload);
              setRetryKey((value) => value + 1);
            }
          : undefined
      }
      onSaveRegulatoryDecision={
        mode === 'review'
          ? async (payload: PatientV2RegulatoryDecisionPayload) => {
              if (!patientId) return;
              await savePatientV2RegulatoryDecision(patientId, payload);
              setRetryKey((value) => value + 1);
            }
          : undefined
      }
      triageItems={triageItems}
      triageLoading={triageLoading}
      triageError={triageError}
      onCreateTriageNote={
        mode === 'review'
          ? async (payload: PatientV2TriageNotePayload) => {
              if (!patientId) return;
              await createPatientV2TriageNote(patientId, payload);
              await refreshTriage();
            }
          : undefined
      }
    />
  );

  return <WideLayout outlet={content} />;
};
